/**
 * UserAvatar — Smart avatar that resolves profile pictures.
 *
 * The current user's picture comes from `pictureUrl` (Auth0) in the profile
 * slice; `src` passes a pre-resolved URL straight through.
 *
 * Falls back to initials (computed from `name`) or a generic user icon.
 */

import { Avatar } from '@/components/ui/primitives/avatar';
import type { AvatarProps } from '@/components/ui/primitives/avatar';
import { getInitials } from '@/lib/initials';
import { useAppSelector } from '@/store/hooks';
import { selectUser } from '@/store/slices/authSlice';
import { selectProfile } from '@/store/slices/userProfileSlice';

export interface UserAvatarProps
  extends Pick<AvatarProps, 'size' | 'className' | 'backgroundColor' | 'alt'> {
  /** Identifies the user; the current user's picture comes from the profile slice. */
  userSub?: string;
  /** Pre-resolved image URL — skips all fetching. */
  src?: string;
  /** Display name used for initials fallback and default alt text. */
  name?: string;
}

export function UserAvatar({
  userSub,
  src,
  name,
  alt,
  ...avatarProps
}: UserAvatarProps) {
  const currentUser = useAppSelector(selectUser);
  const currentProfile = useAppSelector(selectProfile);
  const isCurrentUser = !!(userSub && currentUser?.sub && userSub === currentUser.sub);

  // The current user's picture is a direct URL from Auth0
  const clientPictureUrl =
    isCurrentUser && currentProfile?.pictureUrl
      ? currentProfile.pictureUrl
      : null;

  const initials = name ? getInitials(name) : undefined;

  return (
    <Avatar
      src={src ?? clientPictureUrl ?? undefined}
      initials={initials}
      alt={alt ?? name ?? 'User'}
      {...avatarProps}
    />
  );
}
